// Frontend types for Company Document (unified document entity)

export enum TranscriptionDocumentType {
  // Company-specific document types
  TemplateImportSource = 1,
  Logo = 2,
  Policy = 3,
  // Transcription/Audio document types
  OriginalAudio = 10,
  ProcessedAudio = 11,
  RealtimeTranscript = 12,
  BatchTranscript = 13,
  AIInsights = 14,
  Summary = 15,
  FormattedTranscript = 16,
  Attachment = 17,
  // Generic fallback
  Generic = 99
}

export enum TranscriptionDocumentStatus {
  Uploading = 1,
  Processing = 2,
  Completed = 3,
  Failed = 4,
  Archived = 5,
  Expired = 6
}

export enum TranscriptionDocumentRestrictions {
  None = 0,
  RequireAuthentication = 1,
  RequireCompanyAccess = 2,
  RequireOwnerAccess = 4,
  NoDownload = 8,
  NoSharing = 16,
  RequireEncryption = 32
}

export interface TranscriptionDocument {
  id: string;
  version?: string;
  companyId: string;
  transcriptionSessionId?: string;
  personId?: string;
  sessionCollectionId?: string;
  uploadedByUserId?: string;
  relatedTemplateId?: number;
  documentName: string;
  description?: string;
  documentType: TranscriptionDocumentType;
  documentStatus: TranscriptionDocumentStatus;
  documentUrl: string;
  fileFormat?: string;
  fileSizeBytes?: number;
  duration?: string; // ISO 8601 duration
  audioQuality?: number;
  processedDate?: string;
  processingEngine?: string;
  processingVersion?: string;
  expiryDate?: string;
  isArchived: boolean;
  restrictions: TranscriptionDocumentRestrictions;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateTranscriptionDocumentInput {
  transcriptionSessionId: string;
  documentName: string;
  description?: string;
  documentType: TranscriptionDocumentType;
  version?: string;
  personId?: string;
}

export interface TranscriptionDocumentResult {
  success: boolean;
  message?: string;
  document?: TranscriptionDocument;
  documents?: TranscriptionDocument[];
}

// Helper functions for working with document types
export const getDocumentTypeLabel = (
  type: TranscriptionDocumentType
): string => {
  const labels = {
    [TranscriptionDocumentType.TemplateImportSource]: 'Template Import Source',
    [TranscriptionDocumentType.Logo]: 'Logo',
    [TranscriptionDocumentType.Policy]: 'Policy',
    [TranscriptionDocumentType.OriginalAudio]: 'Original Audio',
    [TranscriptionDocumentType.ProcessedAudio]: 'Processed Audio',
    [TranscriptionDocumentType.RealtimeTranscript]: 'Real-time Transcript',
    [TranscriptionDocumentType.BatchTranscript]: 'Batch Transcript',
    [TranscriptionDocumentType.AIInsights]: 'AI Insights',
    [TranscriptionDocumentType.Summary]: 'Summary',
    [TranscriptionDocumentType.FormattedTranscript]: 'Formatted Transcript',
    [TranscriptionDocumentType.Attachment]: 'Attachment',
    [TranscriptionDocumentType.Generic]: 'Generic'
  };
  return labels[type] || 'Unknown';
};

export const getDocumentStatusLabel = (
  status: TranscriptionDocumentStatus
): string => {
  const labels = {
    [TranscriptionDocumentStatus.Uploading]: 'Uploading',
    [TranscriptionDocumentStatus.Processing]: 'Processing',
    [TranscriptionDocumentStatus.Completed]: 'Completed',
    [TranscriptionDocumentStatus.Failed]: 'Failed',
    [TranscriptionDocumentStatus.Archived]: 'Archived',
    [TranscriptionDocumentStatus.Expired]: 'Expired'
  };
  return labels[status] || 'Unknown';
};

export const getDocumentStatusColor = (
  status: TranscriptionDocumentStatus
): 'default' | 'warning' | 'success' | 'error' => {
  switch (status) {
    case TranscriptionDocumentStatus.Completed:
      return 'success';
    case TranscriptionDocumentStatus.Uploading:
    case TranscriptionDocumentStatus.Processing:
      return 'warning';
    case TranscriptionDocumentStatus.Failed:
    case TranscriptionDocumentStatus.Expired:
      return 'error';
    default:
      return 'default';
  }
};

export const formatFileSize = (bytes?: number): string => {
  if (bytes === undefined || bytes === null) return '';
  if (bytes === 0) return '0 Bytes';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
};

export const formatDuration = (duration?: string): string => {
  if (!duration) return '';

  // Parse ISO 8601 duration (e.g., PT1H2M30S -> 1:02:30)
  const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/;
  const matches = duration.match(regex);

  if (!matches) {
    // If it's not ISO 8601, try to parse as plain seconds
    const seconds = parseFloat(duration);
    if (!isNaN(seconds)) {
      return formatSecondsToTime(seconds);
    }
    return duration; // Return as-is if we can't parse it
  }

  const hours = parseInt(matches[1] || '0', 10);
  const minutes = parseInt(matches[2] || '0', 10);
  const seconds = Math.floor(parseFloat(matches[3] || '0'));

  return formatSecondsToTime(hours * 3600 + minutes * 60 + seconds);
};

// Helper function to format seconds into HH:MM:SS or MM:SS
const formatSecondsToTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  } else {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
};
